import React, { useState } from 'react';
import { useGame } from '../context/GameContext';

export default function Arsenal() {
  const {
    tools, addTool, removeTool, showNotification,
    toolCategories, addToolCategory, renameToolCategory, deleteToolCategory
  } = useGame();

  const [showAdd, setShowAdd] = useState(false);
  const [newTool, setNewTool] = useState({ name: '', url: '', category: '' });
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [confirmDeleteCat, setConfirmDeleteCat] = useState(null);

  const handleAdd = async () => {
    if (!newTool.name.trim()) { setError('Enter a tool name.'); return; }
    if (!newTool.url.trim()) { setError('Enter a URL.'); return; }
    if (!newTool.category) { setError('Select a category.'); return; }
    let url = newTool.url.trim();
    if (!url.startsWith('http')) url = 'https://' + url;
    try {
      await addTool(newTool.name.trim(), url, newTool.category);
      setNewTool({ name: '', url: '', category: toolCategories[0]?.name || '' });
      setShowAdd(false);
      setError('');
      showNotification(`${newTool.name} added to arsenal!`, 'success');
    } catch {
      setError('Failed to add tool.');
    }
  };

  const handleDelete = async (id, name) => {
    if (confirmDelete === id) {
      await removeTool(id);
      setConfirmDelete(null);
      showNotification(`${name} removed.`, 'info');
    } else {
      setConfirmDelete(id);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  const handleRenameCategory = async (id) => {
    if (editingName.trim()) await renameToolCategory(id, editingName.trim());
    setEditingCat(null);
    setEditingName('');
  };

  const handleDeleteCategory = async (id) => {
    if (confirmDeleteCat === id) {
      await deleteToolCategory(id);
      setConfirmDeleteCat(null);
    } else {
      setConfirmDeleteCat(id);
      setTimeout(() => setConfirmDeleteCat(null), 3000);
    }
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    await addToolCategory(newCatName.trim());
    setNewCatName('');
    setShowAddCat(false);
  };

  return (
    <div className="page arsenal-page">
      <div className="arsenal-header">
        <h1 className="page-title">PRODUCTIVITY ARSENAL</h1>
        <div className="home-header-actions">
          <button className="btn-sm" onClick={() => setShowAddCat(p => !p)}>
            + ADD CATEGORY
          </button>
          <button className="btn-primary" onClick={() => setShowAdd(!showAdd)}>
            {showAdd ? 'CANCEL' : '+ ADD TOOL'}
          </button>
        </div>
      </div>

      {showAddCat && (
        <div className="add-cat-form">
          <input
            className="tool-input"
            placeholder="Category name..."
            value={newCatName}
            onChange={e => setNewCatName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
          />
          <button className="btn-primary" onClick={handleAddCategory}>ADD</button>
          <button className="btn-sm btn-cancel" onClick={() => setShowAddCat(false)}>CANCEL</button>
        </div>
      )}

      {showAdd && (
        <div className="add-tool-form">
          <div className="form-title">ADD NEW TOOL</div>
          <div className="form-row">
            <input
              className="tool-input"
              placeholder="Tool name (e.g. Notion AI)"
              value={newTool.name}
              onChange={e => { setNewTool(p => ({ ...p, name: e.target.value })); setError(''); }}
            />
            <input
              className="tool-input"
              placeholder="URL (e.g. notion.so)"
              value={newTool.url}
              onChange={e => { setNewTool(p => ({ ...p, url: e.target.value })); setError(''); }}
            />
            <select
              className="tool-select"
              value={newTool.category}
              onChange={e => setNewTool(p => ({ ...p, category: e.target.value }))}
            >
              <option value="">Select category...</option>
              {toolCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <button className="btn-primary" onClick={handleAdd}>ADD</button>
          </div>
          {error && <div className="form-error">{error}</div>}
        </div>
      )}

      <div className="arsenal-grid">
        {toolCategories.map(cat => {
          const catTools = tools.filter(t => t.category === cat.name);
          return (
            <div key={cat.id} className="arsenal-column">
              <div className="arsenal-cat-header">
                {editingCat === cat.id ? (
                  <input
                    className="cat-rename-input"
                    value={editingName}
                    onChange={e => setEditingName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleRenameCategory(cat.id);
                      if (e.key === 'Escape') setEditingCat(null);
                    }}
                    autoFocus
                  />
                ) : (
                  <span className="arsenal-cat-name">{cat.name.toUpperCase()}</span>
                )}
                <button
                  className="cat-edit-btn"
                  onClick={() => { setEditingCat(cat.id); setEditingName(cat.name); }}
                  title="Rename"
                >
                  ?
                </button>
                {editingCat === cat.id && (
                  <button className="cat-edit-btn" onClick={() => handleRenameCategory(cat.id)}>?</button>
                )}
                <button
                  className={`cat-delete-btn ${confirmDeleteCat === cat.id ? 'confirm' : ''}`}
                  onClick={() => handleDeleteCategory(cat.id)}
                  title={confirmDeleteCat === cat.id ? 'Click again to confirm' : 'Delete category'}
                >
                  {confirmDeleteCat === cat.id ? '!' : 'X'}
                </button>
              </div>
              <div className="tool-list">
                {catTools.length === 0 && (
                  <div className="empty-tools">No tools yet. Add one above.</div>
                )}
                {catTools.map(tool => (
                  <div key={tool.id} className="tool-card">
                    <a href={tool.url} target="_blank" rel="noopener noreferrer" className="tool-link">
                      <span className="tool-name">{tool.name}</span>
                      <span className="tool-arrow">?</span>
                    </a>
                    <button
                      className={`tool-delete ${confirmDelete === tool.id ? 'confirm' : ''}`}
                      onClick={() => handleDelete(tool.id, tool.name)}
                      title={confirmDelete === tool.id ? 'Click again to confirm' : 'Remove tool'}
                    >
                      {confirmDelete === tool.id ? '?' : 'X'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
